/**
 * Zero-Trust In-Memory URL Decoder
 * Prevents PII leakage by recursively decoding URL-encoded demographic and address strings in browser memory.
 */

export function safeDecodeString(val) {
  if (typeof val !== "string") return val;
  if (!val.includes("%") && !val.includes("+")) return val;
  try {
    // Handle both %20 and '+' for spaces
    return decodeURIComponent(val.replace(/\+/g, " "));
  } catch (e) {
    // Fallback if partial/malformed URI component
    try {
      return unescape(val.replace(/\+/g, " "));
    } catch (err) {
      return val;
    }
  }
}

/**
 * Recursively decodes strings in nested objects and arrays
 * @param {any} input - Object, array, or primitive value
 * @param {Set} seen - WeakSet or Set to prevent circular reference recursion
 * @returns {any} Deep copy with all string fields URL-decoded
 */
export function recursiveUrlDecode(input, seen = new Set()) {
  if (input === null || input === undefined) {
    return input;
  }

  if (typeof input === "string") {
    return safeDecodeString(input);
  }

  if (typeof input !== "object") {
    return input;
  }

  if (seen.has(input)) {
    return input;
  }
  seen.add(input);

  if (Array.isArray(input)) {
    return input.map((item) => recursiveUrlDecode(item, seen));
  }

  const result = {};
  for (const [key, val] of Object.entries(input)) {
    result[key] = recursiveUrlDecode(val, seen);
  }
  return result;
}

/**
 * Decodes address object into a single formatted multi-line or comma-separated string
 */
export function formatDecodedAddress(addr) {
  if (!addr) return "-";
  const decoded = recursiveUrlDecode(addr);
  if (typeof decoded === "string") return decoded;

  const parts = [
    decoded.line1 || decoded.address_line1 || decoded.street,
    decoded.line2 || decoded.address_line2,
    decoded.landmark,
    decoded.city,
    decoded.state,
    decoded.pincode || decoded.pin || decoded.postal_code,
    decoded.country,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(", ") : "-";
}
